import { EMPTY, filter, map, take } from 'rxjs';

import type {
  ItinerarySearchCriteria,
  TripSearchCriteria,
  ItineraryResponse,
  SearchService,
  RecipeResponse,
  ItinerarySuggestions,
} from '@libs/trip-planner-interfaces';
import { GraphQL } from '@libs/trip-planner-interfaces';
import { isDefined, performanceMark, PerformancePoint } from '@libs/utils';

import { getApolloClient } from './infrastructure';
import {
  SEARCH_ITINERARIES_SUBSCRIPTION,
  SEARCH_ITINERARY_SUGGESTION_PRICES_SUBSCRIPTION,
} from './queries';
import { logged, zenToRx } from './rx';
import { getRecipeQL } from './utils/requestMappers/recipeQL';
import { toItineraryResponse } from './utils/responseMappers/itineraryResponseQL';
import { toItinerarySuggestionPrices } from './utils/responseMappers/itinerarySuggestionPricesResponseQL';

let isFirstSearch = true;

export class ItinerarySearchService
  implements SearchService<TripSearchCriteria, ItineraryResponse>
{
  public search(criteria: ItinerarySearchCriteria): RecipeResponse {
    if (
      criteria.userCriteria.ingredients === null ||
      criteria.userCriteria?.ingredients.length === 0
    ) {
      return EMPTY;
    }

    const recipeCriteria = getRecipeQL(criteria);

    if (isFirstSearch) {
      performanceMark('FirstItineraryRequested');
      isFirstSearch = false;
    }

    performanceMark(PerformancePoint.ItineraryPricesRequested);

    const itineraryResponse = zenToRx(
      getApolloClient().subscribe<
        GraphQL.searchItinerariesSubscription,
        GraphQL.searchItinerariesSubscriptionVariables
      >({
        variables: {
          recipe: recipeCriteria,
        },
        query: SEARCH_ITINERARIES_SUBSCRIPTION,
      })
    );

    itineraryResponse
      .pipe(take(1))
      .subscribe(() =>
        performanceMark(PerformancePoint.ItineraryPricesReceived)
      );

    return itineraryResponse.pipe(
      logged('Itinerary Response'),
      map((fetchResult) => fetchResult.data),
      filter(isDefined),
      map((response) => toItineraryResponse(response, criteria))
    );
  }

  public searchSuggestions(
    criteria: ItinerarySearchCriteria
  ): ItinerarySuggestions {
    if (
      criteria.userCriteria.ingredients === null ||
      criteria.userCriteria?.ingredients.length === 0
    ) {
      return {
        cheapestSuggestedItineraries: EMPTY,
        mostPopularSuggestedItineraries: EMPTY,
      };
    }

    const baseRecipeCriteria = getRecipeQL(criteria);
    const mostPopularRecipeCriteria: GraphQL.RecipeQL = {
      ...baseRecipeCriteria,
      suggestedBy: undefined,
      ingredients: baseRecipeCriteria.ingredients?.map((ingredient) => ({
        ...ingredient,
        suggestedBy: GraphQL.InputAvailibityOrderQL.Popular,
      })),
    };
    const cheapestRecipeCriteria: GraphQL.RecipeQL = {
      ...baseRecipeCriteria,
      suggestedBy: undefined,
      ingredients: baseRecipeCriteria.ingredients?.map((ingredient) => ({
        ...ingredient,
        suggestedBy: GraphQL.InputAvailibityOrderQL.Cheapest,
      })),
    };

    const mostPopularItineraryResponse = zenToRx(
      getApolloClient().subscribe<
        GraphQL.searchItinerarySuggestionPricesSubscription,
        GraphQL.searchItinerarySuggestionPricesSubscriptionVariables
      >({
        variables: {
          recipe: mostPopularRecipeCriteria,
        },
        query: SEARCH_ITINERARY_SUGGESTION_PRICES_SUBSCRIPTION,
      })
    );
    const cheapestItineraryResponse = zenToRx(
      getApolloClient().subscribe<
        GraphQL.searchItinerarySuggestionPricesSubscription,
        GraphQL.searchItinerarySuggestionPricesSubscriptionVariables
      >({
        variables: {
          recipe: cheapestRecipeCriteria,
        },
        query: SEARCH_ITINERARY_SUGGESTION_PRICES_SUBSCRIPTION,
      })
    );

    return {
      cheapestSuggestedItineraries: cheapestItineraryResponse.pipe(
        map((fetchResult) => fetchResult.data),
        filter(isDefined),
        map((response) => toItinerarySuggestionPrices(response))
      ),
      mostPopularSuggestedItineraries: mostPopularItineraryResponse.pipe(
        map((fetchResult) => fetchResult.data),
        filter(isDefined),
        map((response) => toItinerarySuggestionPrices(response))
      ),
    };
  }
}
